import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { extractErrorMessage, extractJobErrorMessage, resources } from '../../core/services';
import type { DocumentType } from '../../core/types/data-models';

function isConflictError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 409;
}

function GenerateDocumentDialog({
  docType,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  docType: DocumentType | null;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (prompt: string, skipQuestions: boolean, skipEntities: boolean, skipDashboard: boolean) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [skipPrompt, setSkipPrompt] = useState(true);
  const [skipQuestions, setSkipQuestions] = useState(true);
  const [skipEntities, setSkipEntities] = useState(false);
  const [skipDashboard, setSkipDashboard] = useState(true);

  // Reset the form each time a different document type opens the dialog
  useEffect(() => {
    if (docType) {
      setPrompt('');
      setSkipPrompt(true);
      setSkipQuestions(true);
      setSkipEntities(false);
      setSkipDashboard(true);
    }
  }, [docType]);

  const canSubmit = skipPrompt || prompt.trim().length > 0;

  return (
    <Dialog
      open={docType !== null}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Generate {docType?.name ?? 'Document'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add an optional prompt to guide generation, or run without an additional prompt.
        </Typography>
        <TextField
          label="Additional prompt"
          multiline
          minRows={5}
          fullWidth
          placeholder="Describe any specifics you want reflected in this document…"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={skipPrompt || submitting}
        />
        <Stack sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={skipPrompt}
                onChange={(e) => setSkipPrompt(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Run without additional prompt"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={skipQuestions}
                onChange={(e) => setSkipQuestions(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Run without ask questions"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={skipEntities}
                onChange={(e) => setSkipEntities(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Run without extract entities"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={skipDashboard}
                onChange={(e) => setSkipDashboard(e.target.checked)}
                disabled={submitting}
              />
            }
            label="Run without generate dashboard"
          />
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => onSubmit(skipPrompt ? '' : prompt, skipQuestions, skipEntities, skipDashboard)}
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Starting…' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface UseDocumentGenerationResult {
  /** Call from a "Generate <DocType>" button — opens the prompt dialog, or
   * shows a blocked-attempt snackbar if a job is already running. */
  requestGenerate: (docType: DocumentType) => void;
  /** Render once near the page root — the prompt dialog plus the
   * blocked-attempt snackbar share this one node. */
  dialog: ReactNode;
}

/**
 * Shared "Generate <DocumentType>" flow for recommended/locked document-type
 * cards (Next Steps, AI Intelligence Assistants): opens a prompt dialog,
 * enqueues a GENERATE_DOCUMENT (or GENERATE_DOCUMENT_QUESTIONS) job, and
 * pre-empts the attempt with a snackbar when a job is already running for
 * the project — the one-job-per-project guard is still enforced
 * server-side (409), this is just a best-effort UI pre-check to avoid the
 * round trip. One instance per page/project.
 */
export function useDocumentGeneration(projectId: number): UseDocumentGenerationResult {
  const queryClient = useQueryClient();
  const [docType, setDocType] = useState<DocumentType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedNotice, setBlockedNotice] = useState<string | null>(null);

  const activeJobsQuery = useQuery({
    queryKey: ['project-jobs-active', projectId],
    queryFn: () =>
      resources.jobs.list({
        filter: { projectId: [projectId], status: ['PENDING', 'PROCESSING'] },
        pageSize: 20,
        sort: '-id',
      }),
    enabled: Number.isFinite(projectId),
    refetchInterval: (query) => ((query.state.data?.data?.length ?? 0) > 0 ? 3000 : false),
  });
  const isProjectBusy = (activeJobsQuery.data?.data?.length ?? 0) > 0;

  const requestGenerate = (dt: DocumentType) => {
    if (isProjectBusy) {
      setBlockedNotice("Can't start — another job is already running for this project. Try again once it finishes.");
      return;
    }
    setError(null);
    setDocType(dt);
  };

  const handleSubmit = async (prompt: string, skipQuestions: boolean, skipEntities: boolean, skipDashboard: boolean) => {
    if (!docType) return;
    setSubmitting(true);
    setError(null);
    try {
      if (skipQuestions) {
        await resources.generateDocument(projectId, {
          documentType: docType.type,
          generateUserPrompt: prompt,
          skipEntities,
          skipDashboard,
        });
      } else {
        await resources.generateDocumentQuestions(projectId, {
          documentType: docType.type,
          generateUserPrompt: prompt,
          skipEntities,
          skipDashboard,
        });
      }
      setDocType(null);
      queryClient.invalidateQueries({ queryKey: ['project-jobs-active', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-jobs-history', projectId] });
    } catch (e) {
      if (isConflictError(e)) {
        setDocType(null);
        setBlockedNotice(extractJobErrorMessage(e));
      } else {
        setError(extractErrorMessage(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const dialog = (
    <>
      <GenerateDocumentDialog
        docType={docType}
        submitting={submitting}
        error={error}
        onClose={() => setDocType(null)}
        onSubmit={handleSubmit}
      />
      <Snackbar
        open={blockedNotice != null}
        autoHideDuration={5000}
        onClose={() => setBlockedNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setBlockedNotice(null)} sx={{ width: '100%' }}>
          {blockedNotice}
        </Alert>
      </Snackbar>
    </>
  );

  return { requestGenerate, dialog };
}
